import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { AgendaCoverage, AgendaCoverageItem } from '../types';
import { scoreColor } from '../types';
import SectionShell, { CLAMP_THREE_LINES, Figure } from './SectionShell';

const STATUS_LABEL: Record<AgendaCoverageItem['status'], string> = {
  satisfied: 'answered',
  abandoned: 'left unconvinced',
  raised: 'unanswered',
  unlocked: 'never came up',
  locked: 'never came up',
};

const STATUS_COLOR: Record<AgendaCoverageItem['status'], 'success' | 'error' | 'warning' | 'default'> = {
  satisfied: 'success',
  abandoned: 'error',
  raised: 'warning',
  unlocked: 'default',
  locked: 'default',
};

const ConcernRow: React.FC<{ item: AgendaCoverageItem; index: number }> = ({ item, index }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={item.repResponse ? 1 : 0}>
      <Chip label={item.type} size="small" variant="outlined" />
      <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 200 }}>
        {item.concern || `Concern ${index + 1}`}
      </Typography>
      <Chip label={STATUS_LABEL[item.status] ?? item.status} size="small" color={STATUS_COLOR[item.status] ?? 'default'} />
      {item.score != null && <Chip label={`${item.score}%`} size="small" color={scoreColor(item.score)} />}
      {item.pushbackRounds > 0 && (
        <Typography variant="caption" color="text.secondary">
          {item.pushbackRounds} pushback{item.pushbackRounds === 1 ? '' : 's'}
        </Typography>
      )}
    </Box>
    {item.repResponse && (
      <Typography variant="body2" color="text.secondary" sx={CLAMP_THREE_LINES}>
        Rep: {item.repResponse}
      </Typography>
    )}
  </Paper>
);

/** Every knowledge base concern the customer held: whether it came up and how it was answered. */
const AgendaCoverageSection: React.FC<{ data?: AgendaCoverage }> = ({ data }) => {
  if (!data) return null;
  return (
    <SectionShell title="Concerns Handled" score={data.overallScore} summary={data.overallSummary}>
      <Stack direction="row" spacing={4} mb={2} flexWrap="wrap" useFlexGap>
        <Figure label="Came up" value={`${data.raisedCount} of ${data.totalItems}`} />
        <Figure label="Answered" value={data.satisfiedCount} />
        <Figure label="Pushbacks" value={data.totalPushbackRounds} />
        {data.averageAnswerScore != null && <Figure label="Average answer" value={`${Math.round(data.averageAnswerScore)}%`} />}
      </Stack>
      <Stack spacing={1.5}>
        {data.items?.map((item, index) => <ConcernRow key={item.itemId} item={item} index={index} />)}
      </Stack>
    </SectionShell>
  );
};

export default AgendaCoverageSection;
